/**
 * THDMX
 * Custom SWR error-retry policy to prevent retry storms on backend saturation.
 */
import type { SWRConfiguration } from 'swr';

/**
 * Maximum number of automatic SWR retries after an error. SWR invokes
 * `onErrorRetry` with `retryCount` starting at 1, so this allows at most two
 * re-attempts before giving up.
 */
export const MAX_ERROR_RETRY_COUNT = 2;

/** Base interval (ms) used for the exponential backoff between retries. */
export const ERROR_RETRY_INTERVAL = 5000;

/** Maximum random jitter (ms) added to each retry to de-synchronize clients. */
export const JITTER_MS = 1000;

type ApiError = { status?: number; response?: { status?: number } };

const getStatus = (error: unknown): number | undefined =>
	(error as ApiError)?.status ?? (error as ApiError)?.response?.status;

/**
 * Bounded SWR error-retry policy.
 *
 * The OOTB SWR default retries on every error, indefinitely while the hook is
 * mounted. Under backend saturation that turns a slow backend into a retry
 * storm (every product/facet hook on every PLP re-firing in lock-step). This
 * policy instead:
 *  - never retries 404 (the resource genuinely doesn't exist); other 4xx such
 *    as a transient Akamai 403 are still retried,
 *  - caps retries at {@link MAX_ERROR_RETRY_COUNT},
 *  - uses exponential backoff with jitter so retries across many clients do
 *    not arrive as a synchronized wave.
 *
 * Transient (network / 5xx) failures still recover automatically; sustained
 * outages no longer amplify load on the origin.
 */
export const onErrorRetry: NonNullable<SWRConfiguration['onErrorRetry']> = (
	error,
	_key,
	config,
	revalidate,
	opts
) => {
	const status = getStatus(error);

	// Only 404 is treated as non-retryable — the resource genuinely doesn't
	// exist, so a retry just wastes a call. Other 4xx (e.g. a transient 403
	// from Akamai's edge/WAF) can recover, so they are still retried.
	if (status === 404) {
		return;
	}

	// Cap retries so a saturated backend does not receive a retry storm.
	if (opts.retryCount > MAX_ERROR_RETRY_COUNT) {
		return;
	}

	// Exponential backoff with jitter. retryCount starts at 1, so the first
	// retry waits ~base, the second ~base * 2.
	const base = config.errorRetryInterval ?? ERROR_RETRY_INTERVAL;
	const delay = base * 2 ** (opts.retryCount - 1) + Math.random() * JITTER_MS;

	setTimeout(() => revalidate(opts), delay);
};
